import type {FC} from 'react'
import {useMemo, useRef} from 'react'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Blankslate} from '../Blankslate'
import {ActorBypassMode, type BypassActor, type SupportedBypassMode} from '@github-ui/bypass-actors/types'
import {BypassList} from '@github-ui/bypass-actors/BypassList'
import {BypassSelectPanel} from '@github-ui/bypass-actors/BypassSelectPanel'
import type {RulesetRoutePayload, RulesetTarget} from '../../types/rules-types'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {useRuleStrings} from '../../hooks/use-rule-strings'

import {capitalize} from '../../helpers/string'
import {PanelHeader} from './conditions/PanelHeader'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type BypassListPanelProps = {
  readOnly?: boolean
  bypassActors: BypassActor[]
  setBypassActors: (bypassActors: BypassActor[]) => void
  addBypassActor: (
    actorId: BypassActor['actorId'],
    actorType: BypassActor['actorType'],
    preferredAvatarUrl: BypassActor['preferredAvatarUrl'],
    name: BypassActor['name'],
    bypassMode: BypassActor['bypassMode'],
    owner: BypassActor['owner'],
  ) => void
  rulesetTarget: RulesetTarget
  removeBypassActor: (bypassActor: BypassActor) => void
  updateBypassActor: (bypassActor: BypassActor) => void
}

export const BypassListPanel: FC<BypassListPanelProps> = ({
  readOnly,
  bypassActors,
  addBypassActor,
  removeBypassActor,
  rulesetTarget,
  updateBypassActor,
}: BypassListPanelProps) => {
  const {baseAvatarUrl, sourceType} = useRoutePayload<RulesetRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const {rulesetOrPolicy} = useRuleStrings()
  const isUserBypassActorsEnabled = isFeatureEnabled('user_bypass_actors')

  const supportedBypassModes: SupportedBypassMode[] = [
    {
      mode: ActorBypassMode.ALWAYS,
      description: 'The ruleset will be evaluated and the selected actor(s) will be prompted to bypass',
    },
    {
      mode: ActorBypassMode.EXEMPT,
      description: 'The ruleset will not be evaluated and no bypass prompt will be shown',
    },
  ]
  if (rulesetTarget === 'branch') {
    supportedBypassModes.push({
      mode: ActorBypassMode.PRS_ONLY,
      description:
        'The ruleset will be enforced on command line changes and the selected actor(s) will be prompted to bypass only in a pull request',
    })
  }

  const bypassSuggestionsUrl = resolvePath('bypass_suggestions')

  const enabledBypassActors = useMemo(() => {
    return [
      ...bypassActors
        .filter(({_enabled}) => _enabled)
        .sort(a => (a.name === 'admin' && a.actorType === 'RepositoryRole' ? -1 : 0)),
    ]
  }, [bypassActors])

  const bypassSelectPanelAnchorRef = useRef<HTMLButtonElement>(null)

  let listName = 'bypass list'
  let bypassTitle = 'Add bypass'
  let reviewerGroup = 'roles, teams, agents, and apps'
  if (isUserBypassActorsEnabled) {
    reviewerGroup = 'roles, teams, agents, apps, and users'
  }
  if (rulesetTarget === 'repository') {
    listName = 'allow list'
    bypassTitle = 'Add actors'
  }
  if (sourceType === 'enterprise') {
    // Temporarily add 'enterprise' condition
    // until we support apps and teams at the enterprise level
    reviewerGroup = 'roles'
  }

  let subtext = `Exempt ${reviewerGroup} from this ${rulesetOrPolicy} by adding them to the ${listName}.`

  if (rulesetTarget === 'push') {
    // Temporarily add 'enterprise' condition
    // until we support apps and teams at the enterprise level
    if (sourceType === 'enterprise') {
      subtext = `Select the roles that can bypass and also approve bypass requests.`
    } else {
      subtext = `Select the roles and teams that can bypass and also approve bypass requests. Add the bots and agents that can bypass this ${rulesetOrPolicy}.`
    }
  }

  return (
    <div>
      <div className="tmp-mb-3">
        <PanelHeader
          title={`${capitalize(listName)}`}
          subtitle={subtext}
          trailing={
            !readOnly && (
              <div className="ml-auto">
                <BypassSelectPanel
                  title={bypassTitle}
                  baseAvatarUrl={baseAvatarUrl}
                  enabledBypassActors={enabledBypassActors}
                  addBypassActor={addBypassActor}
                  removeBypassActor={removeBypassActor}
                  suggestionsUrl={bypassSuggestionsUrl}
                  addReviewerSubtitle={`Choose which ${reviewerGroup} can bypass this ${rulesetOrPolicy}`}
                  bypassSelectPanelAnchorRef={bypassSelectPanelAnchorRef}
                />
              </div>
            )
          }
        />
      </div>
      <div className="Box">
        {enabledBypassActors.length > 0 ? (
          <ul>
            <BypassList
              readOnly={readOnly}
              removeBypassActor={removeBypassActor}
              refocusOnRemoval={() =>
                setTimeout(() => {
                  bypassSelectPanelAnchorRef.current?.focus()
                })
              }
              supportedBypassModes={supportedBypassModes}
              updateBypassActor={updateBypassActor}
              enabledBypassActors={enabledBypassActors}
              baseAvatarUrl={baseAvatarUrl}
            />
          </ul>
        ) : (
          <Blankslate heading={`${capitalize(listName)} is empty`} />
        )}
      </div>
    </div>
  )
}
